/* eslint-disable react/display-name */
import { InformationCircleIcon } from '@heroicons/react/20/solid'
import { classNames } from '~/utils/client/helpers'
import { Small, Label } from '../text/Text'
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'
import { i18n } from '@lingui/core'

export type InputProps = React.ComponentProps<'input'> & {
  id?: string
  name: string
  label: string
  error?: string | string[] | undefined | null
  helper?: string
  onHelper?: () => void
  neighbourHasHelper?: boolean
  unpasteable?: boolean
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      id,
      label,
      name,
      type = 'text',
      className,
      error,
      helper,
      onHelper,
      neighbourHasHelper = false,
      disabled,
      unpasteable,
      ...inputProps
    }: InputProps,
    ref
  ) => {
    const errorMessage = Array.isArray(error) ? error.join(', ') : error
    const inputId = id ?? name
    const descriptionId = helper ? `${inputId}-description` : undefined
    const errorId = errorMessage ? `${inputId}-error` : undefined
    const describedBy = [descriptionId, errorId].filter(Boolean).join(' ') || undefined

    const inputRef: React.LegacyRef<HTMLInputElement> = useRef(null)
    useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(
      ref,
      () => inputRef.current
    )

    useEffect(() => {
      if (inputRef?.current && unpasteable) {
        inputRef.current.onpaste = (e) => {
          e.preventDefault()
          return false
        }
      }
    }, [unpasteable])

    const inputStyles = classNames(
      'relative block w-full rounded-md border-0 pb-1.5 pt-2.5 pr-2 px-3 sm:text-sm ring-inset ring-1 focus:ring-2 focus:ring-inset',
      error
        ? 'ring-red-300 placeholder:text-red-300 text-red-900 focus:ring-red-500 animate-wiggle-error'
        : 'text-heading shadow-sm ring-border-default placeholder:text-muted focus:ring-brand',
      disabled ? 'bg-surface text-muted cursor-not-allowed' : ''
    )

    const setInvalidMessage = (e: React.InvalidEvent<HTMLInputElement>) => {
      if (!e.target) return
      const { validity } = e.target
      const message = getValidationMessage(validity)
      if (typeof message == 'string') {
        e.target?.setCustomValidity(message)
      }
    }

    const getValidationMessage = (validity: ValidityState) => {
      if (validity.patternMismatch)
        return i18n._('Das Feld {label} entspricht nicht dem benötigen Muster', {
          label: label ?? '',
        })
      if (validity.valueMissing)
        return i18n._('Das Feld {label} ist ein Pflichtfeld', { label: label ?? '' })
      if (validity.tooShort)
        return i18n._('Die Eingabe für {label} ist zu kurz', { label: label ?? '' })
    }

    return (
      <div className={classNames('w-full', className)}>
        {(helper || neighbourHasHelper) && (
          <div
            className={classNames(
              'flex flex-row items-center h-4',
              neighbourHasHelper ? 'h-0 sm:h-4 sm:mb-2' : 'mb-2'
            )}
            aria-hidden={neighbourHasHelper}
          >
            {helper && (
              <button
                type="button"
                className="flex flex-row items-center cursor-pointer focus-ring-brand p-1 -m-1"
                onClick={() => {
                  onHelper?.()
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onHelper?.()
                  }
                }}
                aria-describedby={descriptionId}
                aria-label={`Help for ${label}`}
              >
                <InformationCircleIcon className="h-4 w-4 text-brand text-sm mr-1" />
                <Small id={descriptionId} className="text-brand">
                  {helper}
                </Small>
              </button>
            )}
          </div>
        )}
        <div className={inputStyles}>
          <Label htmlFor={inputId} className="block">
            {label}
          </Label>
          <input
            id={inputId}
            name={name}
            type={type}
            readOnly={disabled}
            ref={inputRef}
            aria-invalid={!!errorMessage}
            aria-describedby={describedBy}
            className={classNames(
              'block w-full border-0 p-0 placeholder-muted placeholder:text-muted focus:ring-0 sm:text-sm sm:leading-6',
              disabled ? 'bg-surface text-muted' : 'bg-white text-heading'
            )}
            onInvalid={setInvalidMessage}
            onInput={(e: React.InvalidEvent<HTMLInputElement>) => e.target.setCustomValidity('')}
            {...inputProps}
          />
        </div>
        {errorMessage && (
          <Small className="mt-2 text-error" id={errorId} role="alert">
            {errorMessage}
          </Small>
        )}
      </div>
    )
  }
)
